import React, { FunctionComponent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LatLong } from 'record-it-utils/location/LocationUtils/LocationUtils';
import { Grid } from '../../grid/Grid/Grid';
import { GridItem } from '../../grid/GridItem/GridItem';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalLifecycleEffect } from '../../hooks/useModalLifecycleEffect/useModalLifecycleEffect';
import { BufferedNumberInput } from '../../input/NumberInput/NumberInput';
import { BasemapMap } from '../../map/BasemapMap/BasemapMap';
import { MapHandle } from '../../map/BasemapMap/MapController/MapController';
import { IconType } from '../../misc/Icon/Icon';
import { BaseDialogIconButton } from '../../modal/BaseDialogIconButton/BaseDialogIconButton';
import {
  BaseDialogTextButton,
  BaseDialogTextButtonType
} from '../../modal/BaseDialogTextButton/BaseDialogTextButton';
import {
  SingleTabBaseDialog,
  SingleTabBaseDialogProps
} from '../../modal/SingleTabBaseDialog/SingleTabBaseDialog';
import { StrictReactNode } from '../../types/StrictReactNode';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { MarkerIcon } from '../MarkerIcon/MarkerIcon';
import styles from './SelectLocationDialog.module.css';

export type SelectLocationDialogProps = PropsWithoutChildren<
  Omit<SingleTabBaseDialogProps, 'title' | 'renderButtons' | 'fullHeight'>
> & {
  location: LatLong | null;
  onAccept: (location: LatLong | null) => void;
  onCancel: () => void;
};

export const SelectLocationDialog: FunctionComponent<SelectLocationDialogProps> =
  React.memo((props) => {
    const { location, opened, onAccept, onCancel, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const [mapHandle, setMapHandle] = useState<MapHandle | null>(null);

    const [displayedCoordinates, setDisplayedCoordinates] =
      useState<NullableLatLong>({
        latitude: null,
        longitude: null
      });

    const { t } = useTranslation('record-it-components');

    const handleAcceptClick = useEventCallback(() => {
      let newLocation: LatLong | null = null;

      if (
        displayedCoordinates.latitude != null &&
        displayedCoordinates.longitude != null
      ) {
        newLocation = {
          latitude: displayedCoordinates.latitude,
          longitude: displayedCoordinates.longitude
        };
      }

      onAccept(newLocation);
    });

    const handleMapMove = useEventCallback((handle: MapHandle) => {
      setDisplayedCoordinates({
        ...handle.getCenter()
      });
    });

    const renderButtons = useCallback((): Array<StrictReactNode> => {
      return [
        <BaseDialogTextButton
          key="cancel"
          type={BaseDialogTextButtonType.Secondary}
          onClick={onCancel}
          data-testid="SelectLocationDialog.CancelButton"
        >
          {t('location.SelectLocationDialog.cancel')}
        </BaseDialogTextButton>,
        <BaseDialogIconButton
          key="accept"
          iconName="check"
          iconType={IconType.Regular}
          className={styles.acceptButton}
          onClick={handleAcceptClick}
          data-testid="SelectLocationDialog.AcceptButton"
        ></BaseDialogIconButton>
      ];
    }, [t, handleAcceptClick, onCancel]);

    const updateMapPosition = useEventCallback(
      (
        handle: MapHandle | null,
        latLong: NullableLatLong,
        animate: boolean = true
      ) => {
        if (latLong.latitude != null && latLong.longitude != null) {
          handle?.setCenter(
            {
              latitude: latLong.latitude,
              longitude: latLong.longitude
            },
            {
              zoom: 18,
              animate
            }
          );
        }
      }
    );

    const handleLatitudeInput = useEventCallback((latitude: number | null) => {
      const latLong: NullableLatLong = {
        longitude: displayedCoordinates.longitude,
        latitude
      };
      setDisplayedCoordinates(latLong);
      updateMapPosition(mapHandle, latLong);
    });

    const handleLongitudeInput = useEventCallback(
      (longitude: number | null) => {
        const latLong: NullableLatLong = {
          longitude,
          latitude: displayedCoordinates.latitude
        };
        setDisplayedCoordinates(latLong);

        updateMapPosition(mapHandle, latLong);
      }
    );

    const handleMapHandleRef = useEventCallback((handle: MapHandle | null) => {
      setMapHandle(handle);
      if (opened) {
        updateMapPosition(handle, displayedCoordinates, false);
      }
    });

    useModalLifecycleEffect({
      opened,
      afterOpened: () => {
        const latLong: NullableLatLong = {
          latitude: location?.latitude ?? null,
          longitude: location?.longitude ?? null
        };

        setDisplayedCoordinates(latLong);
      }
    });

    return (
      <SingleTabBaseDialog
        {...remainingProps}
        opened={opened}
        title={t('location.SelectLocationDialog.title')}
        fullHeight={true}
        renderButtons={renderButtons}
      >
        <Grid className={styles.latLongInputGrid}>
          <GridItem size={6}>
            <BufferedNumberInput
              label={t('location.SelectLocationDialog.latitude')}
              value={displayedCoordinates.latitude}
              onInput={handleLatitudeInput}
              data-testid="SelectLocationDialog.LatitudeInput"
            ></BufferedNumberInput>
          </GridItem>
          <GridItem size={6}>
            <BufferedNumberInput
              label={t('location.SelectLocationDialog.longitude')}
              value={displayedCoordinates.longitude}
              onInput={handleLongitudeInput}
              data-testid="SelectLocationDialog.LongitudeInput"
            ></BufferedNumberInput>
          </GridItem>
        </Grid>
        <div className={styles.mapContainer}>
          <BasemapMap
            refHandle={handleMapHandleRef}
            onMapMove={handleMapMove}
          ></BasemapMap>
          {displayedCoordinates.latitude && displayedCoordinates.longitude ? (
            <MarkerIcon
              className={styles.centerMarker}
              data-testid="SelectLocationDialog.CenterMarker"
            ></MarkerIcon>
          ) : null}
        </div>
      </SingleTabBaseDialog>
    );
  });

type NullableLatLong = {
  latitude: number | null;
  longitude: number | null;
};
